import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Send, MessageCircle, Users, Calendar, Sparkles, ExternalLink, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LegalDisclaimer from '../components/LegalDisclaimer';

const COMMUNITIES = [
  {
    id: 'telegram',
    name: 'Telegram Group',
    description:
      'Member group chat plus real-time educational signals + AI commentary. Link the alerts bot from /alerts.',
    icon: Send,
    color: 'cyan',
    url: 'https://t.me/safehousefinancial',
    cta: 'Join Telegram',
    pro: true,
  },
  {
    id: 'discord',
    name: 'Discord Server',
    description:
      'Voice rooms, channels by category (macro / options / portfolio), and weekly AMAs with instructors.',
    icon: MessageCircle,
    color: 'indigo',
    url: 'https://discord.gg/4nT8Gz3nXh',
    cta: 'Join Discord',
    pro: true,
  },
  {
    id: 'circle',
    name: 'Circle Community',
    description:
      'Long-form discussions, member directory, courses, and curated reading lists. Premium-only.',
    icon: Users,
    color: 'violet',
    url: process.env.REACT_APP_CIRCLE_URL,
    cta: 'Enter Circle',
    pro: true,
  },
  {
    id: 'livestreams',
    name: 'Member Livestreams',
    description:
      'Weekly recorded sessions covering macro briefs, AI-led education, and creator Q&As.',
    icon: Calendar,
    color: 'amber',
    url: process.env.REACT_APP_LIVESTREAM_URL,
    cta: 'See schedule',
    pro: true,
  },
];

const Community = () => {
  const { isPro } = useAuth();
  const navigate = useNavigate();

  const open = (target) => {
    if (target.internal) {
      navigate(target.url);
      return;
    }
    if (!target.url) {
      // No invite URL configured yet — soft fail
      return;
    }
    window.open(target.url, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="rounded-2xl bg-gradient-to-br from-[#07070d] via-[#0a0a14] to-[#0d0d1f] text-white p-8 mb-8 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-50 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.35) 0%, transparent 70%)' }}
          />
          <div className="relative">
            <Badge className="bg-white/10 border border-white/20 text-cyan-300 mb-3">
              <Sparkles size={11} className="mr-1" /> Neon Alpha · Community
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Private community for{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
                serious learners
              </span>
            </h1>
            <p className="text-white/60 text-sm max-w-2xl">
              Telegram group, Discord channels, and member-only livestreams. Pro members get access
              automatically after their Income Blueprint purchase is processed.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {COMMUNITIES.map((c) => {
            const Icon = c.icon;
            const locked = c.pro && !isPro;
            const noUrl = !c.url;
            return (
              <Card
                key={c.id}
                className={`group transition-all hover:shadow-md ${locked ? 'opacity-80' : ''}`}
                data-testid={`community-card-${c.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl bg-${c.color}-100 text-${c.color}-600 flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon size={22} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{c.name}</h3>
                        {c.pro && (
                          <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700">
                            Pro
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3 leading-relaxed">{c.description}</p>
                      <Button
                        size="sm"
                        disabled={locked || noUrl}
                        onClick={() => open(c)}
                        className={`bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-90 text-white`}
                        data-testid={`community-cta-${c.id}`}
                      >
                        {locked ? (
                          <>
                            <Lock size={13} className="mr-1.5" /> Upgrade for access
                          </>
                        ) : noUrl ? (
                          'Invite link coming soon'
                        ) : (
                          <>
                            {c.cta}
                            {!c.internal && <ExternalLink size={12} className="ml-1.5" />}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {!isPro && (
          <Card className="mt-6 border-purple-200 bg-purple-50">
            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900">Unlock all community access with Pro</p>
                <p className="text-xs text-gray-600 mt-1">
                  Membership opens Discord, Telegram, Circle, and livestream replays.
                </p>
              </div>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => navigate('/pro-access')}>
                Get Pro Access
              </Button>
            </CardContent>
          </Card>
        )}

        <LegalDisclaimer variant="membership" />
      </div>
    </div>
  );
};

export default Community;
